/**
 * Centralized application configuration management
 * Handles app name, description, and browser title generation
 */

export interface AppConfig {
  appName: string;
  appDescription: string;
}

// Default app configuration
const DEFAULT_APP_CONFIG: AppConfig = {
  appName: "CheerVue",
  appDescription: "Gym Admin Dashboard",
};

/**
 * Gets the current app configuration from localStorage or returns defaults
 */
export function getAppConfig(): AppConfig {
  if (typeof window === 'undefined') {
    // Server-side rendering - return defaults
    return DEFAULT_APP_CONFIG;
  }

  try {
    const storedSettings = localStorage.getItem("generalSettings");
    if (storedSettings) {
      const settings = JSON.parse(storedSettings);
      
      // Extract only the app config fields we need
      return {
        appName: settings.appName || DEFAULT_APP_CONFIG.appName,
        appDescription: settings.appDescription || DEFAULT_APP_CONFIG.appDescription,
      };
    }
  } catch (error) {
    console.warn("Error loading app config from localStorage:", error);
  }

  return DEFAULT_APP_CONFIG;
}

/**
 * Generates a full app title for browser tabs
 * @param pageTitle - Optional page-specific title
 * @param orgName - Optional organization/gym name
 * @returns Full title in format: "Page Title - Gym Name - CheerVue"
 */
export function generateAppTitle(pageTitle?: string, orgName?: string): string {
  const parts = [];
  
  if (pageTitle) {
    parts.push(pageTitle);
  }
  
  if (orgName) {
    parts.push(orgName);
  }
  
  // Suffix is always CheerVue
  parts.push("CheerVue");
  
  return parts.join(" - ");
}

/**
 * Gets just the base app title without any page-specific prefix
 * @param orgName - Optional organization/gym name
 */
export function getBaseAppTitle(orgName?: string): string {
  if (orgName) {
    return `${orgName} - CheerVue`;
  }
  return "CheerVue";
}

/**
 * Updates the current document title
 * @param pageTitle - Optional page-specific title
 * @param orgName - Optional organization/gym name
 */
export function updateDocumentTitle(pageTitle?: string, orgName?: string): void {
  if (typeof window !== 'undefined') {
    document.title = generateAppTitle(pageTitle, orgName);
  }
}
